import type { TranslationKey } from '../i18n/I18n';

/**
 * Editor hotkeys, resolved from the PHYSICAL key.
 *
 * `event.key` is the character the current layout produces: on a Russian layout Cmd+Z
 * arrives as `я`, so a handler comparing against 'z' never fires and undo looks broken.
 * `event.code` names the key cap itself and is the same in every layout.
 */
export type HotkeyAction = 'undo' | 'redo' | 'delete' | 'duplicate' | 'copy' | 'paste' | 'lock' | 'selectAll' | 'catalog' | 'fit' | 'zoomIn' | 'zoomOut' | 'zoomReset' | 'plan' | 'scene' | 'minimap' | 'shortcuts';

/** `primary` is ⌘ on a Mac and Ctrl elsewhere, matched as either on every system; `shift` left out lets either through. */
export interface HotkeyChord { code: string; primary?: boolean; shift?: boolean }

/** `list` is the row of the list of keys the key shares with others: zoom in and out are one row there. */
export interface Hotkey { action: HotkeyAction; group: 'navigation' | 'objects' | 'edit'; label: TranslationKey; chords: HotkeyChord[]; list?: TranslationKey }

/** Every key of the editor, read by its handler and by whatever names the key, in the order the list of keys shows them. */
export const HOTKEYS: readonly Hotkey[] = [
  { action: 'zoomIn', group: 'navigation', label: '#WebZoomIn', list: '#WebZoomInOut', chords: [{ code: 'Equal', primary: true }, { code: 'NumpadAdd', primary: true }] },
  { action: 'zoomOut', group: 'navigation', label: '#WebZoomOut', list: '#WebZoomInOut', chords: [{ code: 'Minus', primary: true }, { code: 'NumpadSubtract', primary: true }] },
  { action: 'zoomReset', group: 'navigation', label: '#WebZoomTo100', chords: [{ code: 'Digit0', shift: true }] },
  { action: 'fit', group: 'navigation', label: '#EditorFitPlan', chords: [{ code: 'Digit1', shift: true }, { code: 'KeyF' }] },
  { action: 'plan', group: 'navigation', label: '#GoTo2D', chords: [{ code: 'Digit2' }] },
  { action: 'scene', group: 'navigation', label: '#GoTo3D', chords: [{ code: 'Digit3' }] },
  { action: 'minimap', group: 'navigation', label: '#Minimap', chords: [{ code: 'KeyM', shift: false }] },
  { action: 'catalog', group: 'navigation', label: '#BenefitCatalog', chords: [{ code: 'KeyC', shift: false }] },
  { action: 'copy', group: 'objects', label: 'Copy', chords: [{ code: 'KeyC', primary: true }] },
  { action: 'paste', group: 'objects', label: 'Paste', chords: [{ code: 'KeyV', primary: true }] },
  { action: 'duplicate', group: 'objects', label: '#WebDuplicate', chords: [{ code: 'KeyD', primary: true }] },
  { action: 'lock', group: 'objects', label: 'Lock', chords: [{ code: 'KeyL', primary: true, shift: true }] },
  { action: 'delete', group: 'objects', label: 'Delete', chords: [{ code: 'Delete' }, { code: 'Backspace' }] },
  { action: 'selectAll', group: 'objects', label: '#SelectAll', chords: [{ code: 'KeyA', primary: true, shift: false }] },
  { action: 'undo', group: 'edit', label: 'Undo', chords: [{ code: 'KeyZ', primary: true, shift: false }] },
  { action: 'redo', group: 'edit', label: 'Redo', chords: [{ code: 'KeyZ', primary: true, shift: true }, { code: 'KeyY', primary: true }] },
  { action: 'shortcuts', group: 'edit', label: '#WebKeyboardShortcuts', chords: [{ code: 'Slash', primary: true, shift: true }] },
];

/**
 * `event.code` is empty on some keyboards — a tablet's on-screen keyboard, some Bluetooth
 * keyboards on iPadOS, automation — so the key's own name stands in for the cap it names.
 */
const CODE_FROM_KEY: Record<string, string> = {
  Backspace: 'Backspace', Delete: 'Delete', Escape: 'Escape', z: 'KeyZ', Z: 'KeyZ', я: 'KeyZ', Я: 'KeyZ', y: 'KeyY', Y: 'KeyY', н: 'KeyY', Н: 'KeyY',
  d: 'KeyD', D: 'KeyD', в: 'KeyD', В: 'KeyD', f: 'KeyF', F: 'KeyF', а: 'KeyF', А: 'KeyF', '2': 'Digit2', '3': 'Digit3',
  a: 'KeyA', A: 'KeyA', ф: 'KeyA', Ф: 'KeyA', c: 'KeyC', C: 'KeyC', с: 'KeyC', С: 'KeyC', '1': 'Digit1', '!': 'Digit1', '0': 'Digit0', ')': 'Digit0',
  m: 'KeyM', M: 'KeyM', ь: 'KeyM', Ь: 'KeyM', v: 'KeyV', V: 'KeyV', м: 'KeyV', М: 'KeyV', l: 'KeyL', L: 'KeyL', д: 'KeyL', Д: 'KeyL',
  '=': 'Equal', '+': 'Equal', '-': 'Minus', '/': 'Slash', '?': 'Slash',
};
/** A layout that moves + and − off their US keys (German, AZERTY) still zooms by the character they type. */
const ZOOM_FROM_KEY: Record<string, HotkeyAction> = { '+': 'zoomIn', '=': 'zoomIn', '-': 'zoomOut', '_': 'zoomOut' };

export function hotkeyCode(e: Pick<KeyboardEvent, 'code'> & { key?: string }): string {
  return e.code || (e.key !== undefined ? CODE_FROM_KEY[e.key] ?? '' : '');
}

export function editorHotkey(e: Pick<KeyboardEvent, 'code' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'> & { key?: string }): HotkeyAction | null {
  if (e.altKey) return null;
  const code = hotkeyCode(e), primary = e.metaKey || e.ctrlKey;
  const hotkey = HOTKEYS.find(entry => entry.chords.some(chord => chord.code === code && !!chord.primary === primary && (chord.shift === undefined || chord.shift === e.shiftKey)));
  return hotkey?.action ?? (primary && e.key !== undefined ? ZOOM_FROM_KEY[e.key] ?? null : null);
}

export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform ?? '';
  return /mac|iphone|ipad/i.test(platform);
}

// i18n-ignore: key cap names, printed on the keys themselves
const CAPS: Record<string, [mac: string, other: string]> = {
  Equal: ['+', '+'], NumpadAdd: ['+', '+'], Minus: ['−', '−'], NumpadSubtract: ['−', '−'], Slash: ['?', '?'],
  Delete: ['⌦', 'Del'], Backspace: ['⌫', 'Backspace'],
};

/** The caps of every chord of the action, in the platform's order: ⇧⌘Z on a Mac, Ctrl Shift Z elsewhere. */
export function shortcutKeys(action: HotkeyAction, mac = isMacPlatform()): string[][] {
  const hotkey = HOTKEYS.find(entry => entry.action === action);
  return (hotkey?.chords ?? []).map(chord => {
    const cap = CAPS[chord.code]?.[mac ? 0 : 1] ?? chord.code.replace(/^(Key|Digit)/, '');
    const keys = mac ? [chord.shift ? '⇧' : '', chord.primary ? '⌘' : ''] : [chord.primary ? 'Ctrl' : '', chord.shift ? 'Shift' : ''];
    return [...keys.filter(Boolean), cap];
  });
}

/** The action's first chord as one label, the way the menus and the tooltips print it. */
export function shortcutLabel(action: HotkeyAction, mac = isMacPlatform()): string {
  return shortcutKeys(action, mac)[0]?.join(mac ? '' : ' ') ?? '';
}

const ARIA_KEYS: Record<string, string> = { Equal: '=', NumpadAdd: 'Plus', Minus: '-', NumpadSubtract: '-', Slash: '/' };

/** Every chord of the action in `aria-keyshortcuts` form: `Meta+Shift+Z Control+Y`. */
export function shortcutAria(action: HotkeyAction, mac = isMacPlatform()): string {
  return (HOTKEYS.find(entry => entry.action === action)?.chords ?? []).map(chord => [chord.primary ? (mac ? 'Meta' : 'Control') : '', chord.shift ? 'Shift' : '',
    ARIA_KEYS[chord.code] ?? chord.code.replace(/^(Key|Digit)/, '')].filter(Boolean).join('+')).join(' ');
}

/**
 * True when the key press belongs to a text field, an open menu or a list's button, not the editor. A closed
 * menu's button is not one: it holds the focus after a row is picked, and the editor's keys work from there. Nor is
 * a closed list's button for Escape (`code`), the one key it lets go (`Dropdown.tsx`).
 */
export function hotkeyTargetIsText(target: EventTarget | null, code?: string): boolean {
  const el = target as HTMLElement | null;
  if (code === 'Escape' && el?.matches?.('button[role=combobox][aria-expanded=false]')) return false;
  return !!el?.closest?.('input, textarea, select, [contenteditable=true], .dropdown-panel, [role=combobox]');
}

/** What the list of keys is shown over: the floor or ceiling plan, a wall's plan, the 3D orbit or the walk. */
export type ShortcutView = 'plan' | 'wallPlan' | 'orbit' | 'walk';

/**
 * The pointer's own navigation in each view, listed before the keys: on the plan Space or the right button pans
 * (`PlanEditor.tsx`); in the orbit the right button turns (`Viewer.ts`, OrbitControls) and Space pans; the wheel walks.
 */
const GESTURES: Record<ShortcutView, { label: TranslationKey; text: TranslationKey }[]> = {
  plan: [{ label: '#WebShortcutPan', text: '#WebShortcutPanHow' }, { label: '#WebShortcutZoom', text: '#WebShortcutZoomHow' }],
  wallPlan: [{ label: '#WebShortcutZoom', text: '#WebShortcutZoomHow' }],
  orbit: [{ label: '#WebShortcutPan', text: '#WebShortcutPanSpace' }, { label: '#WebShortcutOrbit', text: '#WebShortcutOrbitHow' }, { label: '#WebShortcutZoom', text: '#WebShortcutZoomHow' }],
  walk: [{ label: '#WebShortcutWalk', text: '#WebShortcutWalkHow' }],
};
const FIT_LABELS: Record<ShortcutView, TranslationKey> = { plan: '#EditorFitPlan', wallPlan: '#EditorFitWall', orbit: '#EditorRecenterView', walk: '#EditorRecenterView' };
const EDITING: readonly HotkeyAction[] = ['catalog', 'copy', 'paste', 'lock', 'duplicate', 'delete', 'selectAll', 'undo', 'redo'];

export type ShortcutRow = { label: TranslationKey; keys: string[] } | { label: TranslationKey; text: TranslationKey };

const ZOOM: readonly HotkeyAction[] = ['zoomIn', 'zoomOut', 'zoomReset'];

/** Whether the key does anything in that view: the walk has no zoom (the wheel walks), M opens the minimap only in 3D. */
const actsIn = (action: HotkeyAction, view: ShortcutView, editable: boolean): boolean =>
  (editable || !EDITING.includes(action)) && (ZOOM.includes(action) ? view !== 'walk' : action === 'minimap' ? view === 'orbit' || view === 'walk' : true);

/**
 * The list of keys for the view on screen: the table's groups in order, the gestures first under navigation, a row per
 * key that acts there with each chord as printed (the same label once), the keys sharing a `list` row on one row. The fit
 * row takes the name the menu gives it in that view.
 */
export function shortcutGroups(mac = isMacPlatform(), view: ShortcutView = 'plan', editable = true): { group: Hotkey['group']; rows: ShortcutRow[] }[] {
  return (['navigation', 'objects', 'edit'] as const).map(group => {
    const rows: ShortcutRow[] = group === 'navigation' ? [...GESTURES[view]] : [];
    for (const hotkey of HOTKEYS.filter(entry => entry.group === group && actsIn(entry.action, view, editable))) {
      const label = hotkey.action === 'fit' ? FIT_LABELS[view] : hotkey.list ?? hotkey.label, keys = shortcutKeys(hotkey.action, mac).map(chord => chord.join(mac ? '' : ' '));
      const row = rows.find((entry): entry is { label: TranslationKey; keys: string[] } => entry.label === label && 'keys' in entry);
      const target = row ?? { label, keys: [] };
      if (!row) rows.push(target);
      for (const key of keys) if (!target.keys.includes(key)) target.keys.push(key);
    }
    return { group, rows };
  }).filter(group => group.rows.length);
}
